import { TutorialLayout, CodePreview } from '../../../../shared/components';
import { tutorialLevels } from '../../../../shared/config/tutorials';
import { Alert, AlertDescription } from '@voilajsx/uikit/alert';
import { Card, CardContent } from '@voilajsx/uikit/card';
import { CheckCircle, Zap, Palette, Layout, FolderTree } from 'lucide-react';

const WhatIsUIKit = () => {
  return (
    <TutorialLayout levels={tutorialLevels}>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold mb-4">What is UIKit?</h1>
          <p className="text-lg text-muted-foreground">
            UIKit is a comprehensive React component library designed for rapid application development.
          </p>
        </div>

        <Alert>
          <Zap className="w-4 h-4" />
          <AlertDescription>
            UIKit combines 37+ production-ready components, 6 layouts, 5 themes, and CLI tools into one cohesive system.
          </AlertDescription>
        </Alert>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Core Features</h2>

          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                    <CheckCircle className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">37+ Components</h3>
                    <p className="text-sm text-muted-foreground">
                      Buttons, Cards, Forms, Modals, Tables, and more - all styled consistently.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                    <Layout className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">6 Layouts</h3>
                    <p className="text-sm text-muted-foreground">
                      AdminLayout, PageLayout, AuthLayout, MobileLayout, PopupLayout, BlankLayout.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                    <Palette className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">5 Themes</h3>
                    <p className="text-sm text-muted-foreground">
                      Default, Ocean, Forest, Sunset, Midnight - each with light and dark modes.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                    <FolderTree className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">4 Templates</h3>
                    <p className="text-sm text-muted-foreground">
                      Single page, SPA, Multi-page, and FBCA architectures.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">How It Works</h2>
          <p className="text-muted-foreground">
            UIKit follows a simple architecture:
          </p>

          <div className="bg-muted/50 rounded-lg p-6 font-mono text-sm">
            <div className="space-y-2">
              <div><span className="text-primary">ThemeProvider</span> → Wraps your app, provides theme context</div>
              <div className="pl-4"><span className="text-primary">Layout</span> → Page structure (header, sidebar, content)</div>
              <div className="pl-8"><span className="text-primary">Components</span> → UI elements (Button, Card, etc.)</div>
            </div>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Quick Example</h2>

          <CodePreview
            title="Basic Usage"
            code={`import { ThemeProvider } from '@voilajsx/uikit/theme-provider';
import { Button } from '@voilajsx/uikit/button';

function App() {
  return (
    <ThemeProvider defaultTheme="default" defaultMode="light">
      <Button>Click me</Button>
    </ThemeProvider>
  );
}`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Why UIKit?</h2>

          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <strong>Fast Setup</strong> - Go from zero to working app in 30 seconds with CLI scaffolding.
              </div>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <strong>Consistent Design</strong> - All components follow the same design language.
              </div>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <strong>Production Ready</strong> - Built-in SEO pre-rendering and image optimization.
              </div>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <strong>Tailwind CSS</strong> - Built on Tailwind v4 for easy customization.
              </div>
            </li>
          </ul>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">What You'll Learn</h2>
          <p className="text-muted-foreground">
            This tutorial series covers everything from basics to production deployment:
          </p>

          <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
            <li><strong className="text-foreground">Foundation</strong> - Installation and core concepts</li>
            <li><strong className="text-foreground">Theming</strong> - Colors, themes, and dark mode</li>
            <li><strong className="text-foreground">Layouts</strong> - Page structures and navigation</li>
            <li><strong className="text-foreground">Architecture</strong> - Project templates and patterns</li>
            <li><strong className="text-foreground">Production</strong> - Deployment and optimization</li>
          </ol>
        </section>
      </div>
    </TutorialLayout>
  );
};

export default WhatIsUIKit;
